import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, LogOut, Shield, LayoutDashboard, Compass, MessageCircle, Menu, Flame, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const links = user
    ? [
        { to: "/swipe", label: "Swipe", icon: Flame },
        { to: "/matches", label: "Matches", icon: Heart },
        { to: "/discover", label: "Discover", icon: Compass },
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/chat", label: "Chat", icon: MessageCircle },
        { to: "/safety", label: "Safety", icon: Shield },
      ]
    : [];

  return (
    <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center">
            <Users className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <span className="font-display text-2xl font-bold text-charcoal tracking-tight">Saathly</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 flex items-center gap-2 ${
                loc.pathname === l.to ? "bg-terracotta/10 text-terracotta" : "text-charcoal/70 hover:text-terracotta"
              }`}>
              <l.icon className="w-4 h-4" strokeWidth={1.5} /> {l.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin"
              className="px-4 py-2 rounded-full text-sm font-semibold text-charcoal/70 hover:text-terracotta transition-colors duration-200">
              Admin
            </Link>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")}
                className="rounded-full text-charcoal hover:text-terracotta hover:bg-terracotta/5">Log in</Button>
              <Button data-testid="nav-join-btn" onClick={() => navigate("/register")}
                className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white px-5 transition-transform duration-200 hover:-translate-y-0.5">
                Join free
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2">
                  <img src={user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${user.name}`}
                    alt="me" className="w-9 h-9 rounded-full object-cover border-2 border-terracotta/30" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-3 py-2">
                  <p className="font-semibold text-sm text-charcoal">{user.name}</p>
                  <p className="text-xs text-charcoal/50">{user.time_credits_minutes || 0} min credits</p>
                </div>
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">Settings & Privacy</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/packages")} data-testid="menu-packages">Buy credits</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout"
                  className="text-signal"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            <Menu className="w-6 h-6 text-charcoal" />
          </button>
        </div>
      </div>
      {open && user && (
        <div className="md:hidden border-t border-border bg-white px-5 py-3 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-charcoal/80 flex items-center gap-2">
              <l.icon className="w-4 h-4" /> {l.label}
            </Link>
          ))}
          {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-charcoal/80">Admin</Link>}
        </div>
      )}
    </nav>
  );
}
